import dermatoApi, { type DocumentDownloadResult } from '../../api/dermatoApi';
import type { DocumentLanguage } from '../../hooks/useLanguage';

/**
 * Documentele medicale descărcabile (§1.2.6) din formularul dermato.
 * Fiecare are un endpoint propriu în backend (secțiunea S9b).
 */
export type MedicalDocumentKind = 'referral-dermato' | 'referral-surgery' | 'commission-sheet' | 'screening-letter';

export const MEDICAL_DOCUMENT_LABELS: Record<MedicalDocumentKind, string> = {
  'referral-dermato': 'Scrisoare către dermatologie',
  'referral-surgery': 'Scrisoare către chirurg',
  'commission-sheet': 'Fișă comisie oncologică',
  'screening-letter': 'Scrisoare medicală — screening fără leziuni',
};

/** Nume de rezervă pentru fișier, folosit dacă backendul nu trimite `Content-Disposition`. */
const MEDICAL_DOCUMENT_FILE_SLUGS: Record<MedicalDocumentKind, string> = {
  'referral-dermato': 'scrisoare-dermatologie',
  'referral-surgery': 'scrisoare-chirurgie',
  'commission-sheet': 'fisa-comisie-oncologica',
  'screening-letter': 'scrisoare-screening-fara-leziuni',
};

/** Mesaj de rezervă, în română, în cazul în care backendul nu trimite unul. */
export const DOCUMENT_FALLBACK_MESSAGE =
  'Limba cerută nu este încă disponibilă pentru acest document; a fost generată versiunea în română.';

/** Tooltip pentru butonul de comisie, când decizia nu e încă înregistrată. */
export const COMMISSION_NOT_RECORDED_TOOLTIP = 'Decizia comisiei nu este încă înregistrată';

/** Tooltip pentru butoanele care au nevoie de o leziune. */
export const LESION_REQUIRED_TOOLTIP = 'Selectează o leziune pentru a genera documentul.';

const DOWNLOADERS: Record<
  MedicalDocumentKind,
  (id: string, lang?: DocumentLanguage, lesionId?: string) => Promise<DocumentDownloadResult>
> = {
  'referral-dermato': (id, lang, lesionId) => dermatoApi.downloadReferralDermatology(id, lang, lesionId),
  'referral-surgery': (id, lang, lesionId) => dermatoApi.downloadReferralSurgery(id, lang, lesionId),
  'commission-sheet': (id, lang, lesionId) => dermatoApi.downloadCommissionSheet(id, lang, lesionId),
  'screening-letter': (id, lang) => dermatoApi.downloadScreeningLetter(id, lang),
};

/** Salvează blob-ul în browser, cu numele de fișier dorit. */
export const downloadBlob = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
};

/**
 * Descarcă unul dintre cele 3 documente medicale (§1.2.6).
 *
 * Numele fișierului vine din `Content-Disposition` (dacă backendul îl trimite);
 * altfel se generează un nume de rezervă de forma `fisa-comisie-oncologica-<id>.pdf`.
 * Returnează rezultatul complet (blob + metadata), ca UI-ul să poată afișa
 * bannerul de fallback când limba documentului a fost ajustată.
 */
export const downloadMedicalDocument = async (
  kind: MedicalDocumentKind,
  args: { formId: string; lang?: DocumentLanguage; lesionId?: string },
): Promise<DocumentDownloadResult> => {
  const { formId, lang, lesionId } = args;
  const result = await DOWNLOADERS[kind](formId, lang, lesionId);
  const fileName =
    result.fileName?.trim() || `${MEDICAL_DOCUMENT_FILE_SLUGS[kind]}-${formId}.pdf`;
  downloadBlob(result.blob, fileName);
  return result;
};
